import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { MailCheck } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { useApp } from "../context/AppContext";
import { Logo } from "../components/Logo";
import { tx, useLang } from "../lib/i18n";

const field = "w-full rounded-xl border border-[var(--mv-border-2)] bg-[var(--mv-bg)] px-4 py-3 text-sm outline-none focus:border-[var(--mv-teal)] transition-colors";

function Card({ testid, title, children }) {
  return (
    <div className="mv-container flex min-h-[70vh] items-center justify-center py-16" data-testid={testid}>
      <div className="w-full max-w-md rounded-3xl border border-[var(--mv-border)] bg-[var(--mv-card)] p-8">
        <Logo compact />
        <h1 className="mt-8 text-2xl font-semibold">{title}</h1>
        <div className="mt-5 space-y-3">{children}</div>
      </div>
    </div>
  );
}

export function ForgotPassword() { useLang();
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api.post("/auth/forgot-password", { email }); setSent(true); } catch (e2) { toast.error(tx(errMsg(e2))); }
    setBusy(false);
  };
  return (
    <Card testid="forgot-password-page" title={tx("Reset your password")}>
      {sent ? <p className="text-sm text-[var(--mv-muted)]" data-testid="forgot-sent">{tx("If an account exists for this email, a reset link has been sent. Check your inbox and spam folder.")}</p> : (
        <form onSubmit={submit} className="space-y-3">
          <p className="text-sm text-[var(--mv-muted)]">{tx("Enter your account email and we'll send you a secure link to choose a new password.")}</p>
          <input required type="email" className={field} placeholder={tx("Email")} value={email} onChange={(e) => setEmail(e.target.value)} data-testid="forgot-email-input" />
          <button disabled={busy} className="mv-btn-primary w-full rounded-xl py-3.5 text-sm disabled:opacity-60" data-testid="forgot-submit">{tx(busy ? "Sending…" : "Send reset link")}</button>
        </form>
      )}
      <Link to="/login" className="block text-center text-xs text-[var(--mv-teal)]" data-testid="forgot-back-login">{tx("← Back to sign in")}</Link>
    </Card>
  );
}

export function ResetPassword() { useLang();
  const [sp] = useSearchParams();
  const [f, setF] = useState({ password: "", confirm: "" });
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault(); setErr("");
    if (f.password !== f.confirm) return setErr(tx("Passwords don't match"));
    try { await api.post("/auth/reset-password", { token: sp.get("token") || "", password: f.password }); setDone(true); } catch (e2) { setErr(tx(errMsg(e2))); }
  };
  return (
    <Card testid="reset-password-page" title={tx("Choose a new password")}>
      {done ? (<><p className="text-sm text-[var(--mv-muted)]" data-testid="reset-done">{tx("Your password has been changed. You can now sign in.")}</p>
        <Link to="/login" className="mv-btn-primary block rounded-xl py-3 text-center text-sm" data-testid="reset-go-login">{tx("Sign in")}</Link></>) : (
        <form onSubmit={submit} className="space-y-3">
          <input required minLength={8} type="password" className={field} placeholder={tx("New password (8+ characters)")} value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="reset-password-input" />
          <input required minLength={8} type="password" className={field} placeholder={tx("Confirm new password")} value={f.confirm} onChange={(e) => setF({ ...f, confirm: e.target.value })} data-testid="reset-confirm-input" />
          {err && <p className="text-sm text-red-400" data-testid="reset-error">{err}</p>}
          <button className="mv-btn-primary w-full rounded-xl py-3.5 text-sm" data-testid="reset-submit">{tx("Save new password")}</button>
          <Link to="/forgot-password" className="block text-center text-xs text-[var(--mv-teal)]">{tx("Request a new link")}</Link>
        </form>
      )}
    </Card>
  );
}

export function VerifyEmail() { useLang();
  const [sp] = useSearchParams();
  const { checkAuth, user, setGate } = useApp();
  const [state, setState] = useState("ready");
  const [err, setErr] = useState("");
  const verify = () => {
    setState("working");
    api.post("/auth/verify-email", { token: sp.get("token") || "" })
      .then(() => { setState("ok"); checkAuth && checkAuth(); })
      .catch((e) => { setErr(errMsg(e)); setState("fail"); });
  };
  const resend = () => api.post("/auth/resend-verification").then((r) => toast.success(tx(r.data.already_verified ? "Your email is already verified." : "A new verification email is on its way."))).catch((e) => toast.error(tx(errMsg(e))));
  return (
    <Card testid="verify-email-page" title={tx("Email verification")}>
      {state === "ready" && <><p className="text-sm text-[var(--mv-muted)]">{tx("Confirm the email address for your MIVO account.")}</p>
        <button onClick={verify} className="mv-btn-primary block w-full rounded-xl py-3 text-sm" data-testid="verify-confirm-button">{tx("Verify my email")}</button></>}
      {state === "working" && <p className="text-sm text-[var(--mv-muted)]">{tx("Verifying…")}</p>}
      {state === "ok" && <><p className="flex items-center gap-2 text-sm text-[var(--mv-teal)]" data-testid="verify-ok"><MailCheck className="h-4 w-4" />{tx("Your MIVO email has been verified successfully.")}</p>
        <Link to="/" className="mv-btn-primary block rounded-xl py-3 text-center text-sm" data-testid="verify-continue-mivo">{tx("Continue to MIVO")}</Link></>}
      {state === "fail" && <><p className="text-sm text-red-400" data-testid="verify-fail">{tx(err)}</p>
        <p className="text-xs text-[var(--mv-dim)]">{tx("This verification link expires 24 hours after it is sent.")}</p>
        {user ? <button onClick={resend} className="mv-btn-primary block w-full rounded-xl py-3 text-sm" data-testid="verify-resend">{tx("Resend Verification Email")}</button>
          : <button onClick={() => setGate(true)} className="mv-btn-primary block w-full rounded-xl py-3 text-sm" data-testid="verify-back-login">{tx("Back to Login")}</button>}</>}
      <Link to="/shop" className="mv-btn-outline block rounded-xl py-3 text-center text-sm" data-testid="verify-continue">{tx("Continue Shopping")}</Link>
    </Card>
  );
}

export function VerifyEmailNotice() { useLang();
  const { user } = useApp();
  const [busy, setBusy] = useState(false);
  if (!user || user.email_verified !== false) return null;
  const resend = async () => {
    setBusy(true);
    try { await api.post("/auth/resend-verification"); toast.success(tx("Verification email sent")); } catch (e) { toast.error(tx(errMsg(e))); }
    setBusy(false);
  };
  return (
    <div className="mb-5 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-amber-300/40 bg-amber-300/10 px-4 py-3 text-xs text-amber-200" data-testid="verify-email-notice">
      <span>{tx("Please verify your email address. Check your inbox for the MIVO link — checkout unlocks once verified.")}</span>
      <button disabled={busy} onClick={resend} className="rounded-full border border-amber-300/50 px-3 py-1.5 hover:bg-amber-300/20 disabled:opacity-50" data-testid="verify-resend-button">{tx("Resend email")}</button>
    </div>
  );
}
